// TextBlock — a piece of copy. `dvarId` will reference a DVar id once
// the DVar resolution is plumbed into the embed endpoint (step 9). Until
// then we accept a literal `text` prop so layouts can wire defaults
// without authoring DVars first; once step 9 lands, the resolved text
// from the wire payload replaces `text` and `dvarId` becomes mandatory
// for authored copy.

import {
  resolveContentSource,
  type ContentSource,
  type ContentSourceContext,
} from "./contentSource";
import { linkify } from "../linkify";
import type { BlockUniversalProps } from "./types";

export type TextVariant =
  | "eyebrow"
  | "heading"
  | "heading-display"
  | "subtitle"
  | "body"
  | "body-multiline";

/** @deprecated Use ContentSource from ./contentSource — kept as an alias so
 *  existing imports don't break. The content-source set is now shared by every
 *  string block, not just text. */
export type TextSource = ContentSource;

export interface TextBlockProps extends BlockUniversalProps {
  /** Resolved literal (the layout default, or the server-resolved DVar text).
   *  Used when `source` is "literal"/"dvar". */
  text?: string;
  /** Informational — the server resolved it into `text` before sending. */
  dvarId?: number;
  /** Visual treatment. */
  variant?: TextVariant;
  /** Content source — literal / dvar / from-active-series-* / selected product.
   *  See ./contentSource. */
  source?: ContentSource;
  /** Live content-source context (series + selection), handed down by Slot. */
  sourceCtx?: ContentSourceContext;
}

export function TextBlock({
  text,
  variant = "body",
  themeVariant,
  className,
  source = "literal",
  sourceCtx,
}: TextBlockProps) {
  const resolved = resolveContentSource(source, text, sourceCtx ?? {});
  if (!resolved) return null;
  // h1 for the display heading so screen-reader users hit the visually-
  // largest block first; h2 for inline headings; h3 for subtitles.
  // body-multiline renders as a paragraph too, but its CSS preserves the
  // operator's line breaks (white-space: pre-line) so multi-line copy keeps its
  // shape. The other variants collapse whitespace like normal prose.
  const Tag: keyof JSX.IntrinsicElements =
    variant === "heading-display" ? "h1"
    : variant === "heading" ? "h2"
    : variant === "subtitle" ? "h3"
    : "p";
  return (
    <Tag
      className={"sd-block-text sd-block-text--" + variant + (className ? " " + className : "")}
      data-variant={themeVariant}
    >
      {/* Auto-linkify URLs / emails / phone numbers in the copy so operator
          text like "email order@acme.com" becomes clickable without markup. */}
      {linkify(resolved)}
    </Tag>
  );
}
